import { Inject, Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { GoogleAuthProvider, OAuthCredential } from 'firebase/auth';
import { from, Observable } from 'rxjs';
import { Platform } from '@ionic/angular';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { Environment } from '@viso/core/models';
import axios from 'axios';
import { ENVIRONMENT } from '@viso/core/data-access';

interface GoogleLoginResponse {
  provider: 'google';
  result: {
    accessToken: {
      token: string;
      expires: string;
    } | null;
    idToken: string | null;
    profile: {
      email: string | null;
      familyName: string | null;
      givenName: string | null;
      id: string | null;
      name: string | null;
      imageUrl: string | null;
    };
  };
}

interface AppleLoginResponse {
  provider: string;
  result: {
    idToken: string;
    accessToken: {
      token: string;
    } | null;
    profile: {
      email?: string;
      givenName?: string;
      familyName?: string;
      user: string;
    };
  };
}

@Injectable({
  providedIn: 'root',
})
export class FireAuthService {
  constructor(
    private afAuth: AngularFireAuth,
    private platform: Platform,
    @Inject(ENVIRONMENT) private environment: Environment
  ) {
    this.initializeSocialLogin();
  }

  public async initializeSocialLogin() {
    const commonConfig: {
      google: {
        webClientId: string;
        iOSClientId: string;
        mode: 'online' | 'offline';
      };
      apple?: {
        clientId: string;
      };
    } = {
      google: {
        webClientId: this.environment.googleWebClientId, // Use Web Client ID for all platforms
        iOSClientId: this.environment.googleIOSClientId, // For iOS
        mode: 'online' as 'online' | 'offline' // Replaces grantOfflineAccess
      }
    };
    if (!this.platform.is('capacitor') && (this.platform.is('pwa') || this.platform.is('desktop') || this.platform.is('mobileweb') || this.platform.is('ios'))) {
      commonConfig.apple = {
        clientId: this.environment.appleClientId // For Apple Sign In
      };
    }
    await SocialLogin.initialize(commonConfig);
  }

  get authState(): Observable<any> {
    return this.afAuth.authState;
  }

  async appleLogin() {
    try {
      const appleUser = await SocialLogin.login({
        provider: 'apple',
        options: {
          scopes: ['email', 'name']
        }
      }) as AppleLoginResponse;

      if (!appleUser || !appleUser.result) {
        throw new Error('No apple user data received');
      }

      const authResult = appleUser.result;

      // console.log(authResult);

      const idToken = authResult.idToken;
      const accessToken = authResult.accessToken?.token;

      if (!idToken) {
        throw new Error('No ID token received from Google');
      }

      if (!accessToken) {
        throw new Error('No ID token received from Google');
      }

      const profile = authResult.profile;

      const userData = {
        id: profile.user,
        idToken: accessToken,
        accessToken: idToken,
        name: profile.givenName + ' ' + profile.familyName || '',
        email_id: profile.email || '',
        profile_image: '',
      };
      
      return userData;
      
    } catch (error) {
      throw error;
    }
  }

  async googleLogin() {
    if (!this.platform.is('capacitor') && (this.platform.is('pwa') || this.platform.is('desktop') || this.platform.is('mobileweb'))){
      const provider = new GoogleAuthProvider();
      return from(this.afAuth.signInWithPopup(provider))
        .toPromise()
        .then((result) => {
          const userData = {
            id: result.user.uid,
            idToken: (result.credential as OAuthCredential)?.idToken || 0,
            accessToken:
              (result.credential as OAuthCredential)?.accessToken || 0,
            name: result.user?.displayName || '',
            email_id: result.user?.email || '',
            profile_image: result.user?.photoURL || '',
          };
          return userData;
        })
        .catch((error) => {
          throw error;
        });
    } else {
      return this.googleLoginNative(); 
    }
  }

  private async googleLoginNative() {
    try {
      const googleUser = await SocialLogin.login({
        provider: 'google',
        options: {
          scopes: ['email', 'profile'],
          forcePrompt: true,
          forceRefreshToken: true // if you need refresh token
        }
      }) as GoogleLoginResponse;

      if (!googleUser || !googleUser.result) {
        throw new Error('No Google user data received');
      }

      const authResult = googleUser.result;

      // console.log(authResult);

      const idToken = authResult.idToken;
      const accessToken = authResult.accessToken?.token;

      if (!idToken) {
        throw new Error('No ID token received from Google');
      }

      if (!accessToken) {
        throw new Error('No ID token received from Google');
      }

      const userData = await this.getUserProfileData(accessToken, idToken);
      if (authResult.profile.email == userData.email_id){
        return  userData;
      } else {
        throw new Error('Invalid login attempt');
      }
    } catch (error) {
      throw error;
    }
  }

  async getUserProfileData(accessToken: string, idToken: string) {
    try {
      const response = await axios.get(
        'https://www.googleapis.com/oauth2/v3/userinfo',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        },
      );

      const data = response.data;

      const userData = {
        id: data.sub,
        idToken: accessToken,
        accessToken: idToken,
        name: data.name || '',
        email_id: data.email || '',
        profile_image: data.picture || '',
      };
      return userData;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  }

  logout() {

    if (!this.platform.is('capacitor') && (this.platform.is('pwa') || this.platform.is('desktop') || this.platform.is('mobileweb'))){
      return from(this.afAuth.signOut())
        .toPromise()
        .then(() => {
          console.log('Firebase sign-out successful (Web)');
        })
        .catch((error) => {
          console.error('Firebase sign-out failed (Web)', error);
        });
    } else {
      return SocialLogin.logout({ provider: 'google' })
        .then(() => {
          return this.afAuth.signOut();
        })
        .catch((error) => {
          console.error('Google sign-out failed (Native)', error);
        });
    }
  }
}
